import { message } from 'antd';
import { AxiosInstance } from 'axios';

import FileData from '@/model/General/FileData';
import { ShippingLabel } from '@/model/Logistic/ShippingLabel/ShippingLabel';

import PackageApi from './PackageApi';

// Metadata: evita valores vacíos en descripciones armadas desde campos opcionales del formulario.
const formatDescriptionPart = (value?: unknown) => (typeof value === 'string' ? value : '').trim();
// Metadata: arma una dirección legible contemplando que número y complemento pueden no estar.
const buildAddressDescription = packageData => {
  const address = [
    formatDescriptionPart(packageData.receiver_address),
    formatDescriptionPart(packageData.receiver_address_number),
  ]
    .filter(Boolean)
    .join(' ');
  const details = [
    address,
    formatDescriptionPart(packageData.receiver_address_complement),
    formatDescriptionPart(packageData.receiver_name),
    formatDescriptionPart(packageData.receiver_phone),
    formatDescriptionPart(packageData.receiver_email),
  ];

  return details.filter(Boolean).join(' - ');
};

class PackageRepository {
  packageApi: PackageApi;

  constructor(axiosInstance: AxiosInstance) {
    this.packageApi = new PackageApi(axiosInstance);
  }

  /**
   * Metadata: transforma los campos receiver_* del formulario en el payload de direcciones guardadas.
   */
  saveNewAddress = async packageData => {
    const shippingLabel: ShippingLabel = {
      address: packageData.receiver_address,
      address_number: packageData.receiver_address_number,
      address_complement: packageData.receiver_address_complement,
      email: packageData.receiver_email,
      phone_number: packageData.receiver_phone,
      localidad: packageData.receiver_localidad,
      postal_code: packageData.receiver_postal_code,
      company: packageData.company,
      contact: packageData.receiver_name,

      description: buildAddressDescription(packageData),
    };

    try {
      const response = await this.packageApi.createAddress(shippingLabel);

      return response.data;
    } catch (error) {
      console.error('Error saving address:', error);
      throw error;
    }
  };

  printShippingLabel(packageId: number, resource?: string, params?: Record<string, unknown>) {
    return this.packageApi
      .printShippingLabel(packageId, resource, params)

      .then(() => message.success('Etiqueta de envío generada correctamente'))
      .catch(() => message.error('Error al generar la etiqueta de envío'));
  }

  getLocationFromCEP = async (postalCode: string) => {
    try {
      const response = await this.packageApi.getLocationFromCEP(postalCode);
      const { city, street } = response;
      const { provincia } = city;

      return {
        ciudad: city,
        provincia: provincia,
        direccion: street,
      };
    } catch (error) {
      console.error('Error fetching location from CEP:', error);
      throw error;
    }
  };

  /**
   * Metadata: gestiona la subida y eliminación de archivos de un paquete.
   * Centralizado aquí porque el endpoint /packages_photos/ es genérico y
   * no pertenece a una entidad específica (envíos o recepciones).
   * @param description - Código opcional que identifica el tipo de archivo (ej: tax_file_{id})
   */
  saveFiles = async (files: FileData[], packageId: number, description?: string) => {
    try {
      const filesToUpload = files.filter(file => !file.removed && file.blobFile);
      const filesToRemove = files.filter(file => file.id && file.removed);
      const promises: Promise<any>[] = [];

      if (filesToUpload.length > 0) {
        promises.push(this.packageApi.uploadFiles(filesToUpload, packageId, description));
      }

      if (filesToRemove.length > 0) {
        promises.push(this.packageApi.removeFiles(filesToRemove));
      }

      await Promise.all(promises);
    } catch (error) {
      console.error('Error saving package files:', error);
      message.error('Error al guardar los archivos del paquete');
      throw error;
    }
  };
}

export default PackageRepository;
